/**
 * useAutoSave - 表单自动保存到 localStorage
 *
 * 用法:
 *   const [phone, setPhone] = useAutoSave('login_phone', '');
 *   const [code, setCode] = useAutoSave('login_code', '');
 *
 *   // 或者完整 form:
 *   const [form, setForm, resetForm] = useAutoSave('new_ticket', {
 *     category: '产品', subject: '', content: ''
 *   });
 *
 *   // 用 setForm({...form, subject: 'new'})
 *   // 或单独 setForm(prev => ({...prev, subject: 'new'}))
 */
import { useState, useEffect, useCallback, useRef } from 'react';

export function useAutoSave(key, initialValue) {
  const STORAGE_KEY = `fucai3d_autosave_${key}`;
  const isLoaded = useRef(false);

  // 初始值(优先 localStorage,然后 initialValue)
  const [value, setValue] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && parsed.__ts) {
          return parsed.data ?? initialValue;
        }
      }
    } catch {}
    return initialValue;
  });

  // 写入 localStorage(防抖)
  useEffect(() => {
    if (!isLoaded.current) {
      isLoaded.current = true;
      return;
    }
    const t = setTimeout(() => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          __ts: Date.now(),
          data: value,
        }));
      } catch (e) {
        // localStorage 满了,清掉旧的
        console.warn('useAutoSave save failed:', e);
      }
    }, 500);
    return () => clearTimeout(t);
  }, [key, value]);

  // 重置
  const reset = useCallback(() => {
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
    setValue(initialValue);
  }, [STORAGE_KEY, initialValue]);

  // 强制保存(立即)
  const flush = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        __ts: Date.now(),
        data: value,
      }));
    } catch {}
  }, [STORAGE_KEY, value]);

  return [value, setValue, { reset, flush }];
}

/**
 * 检查 key 的保存时间和大小
 */
export function getAutoSaveInfo(key) {
  try {
    const raw = localStorage.getItem(`fucai3d_autosave_${key}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      timestamp: parsed.__ts,
      age_minutes: (Date.now() - parsed.__ts) / 60000,
      size: raw.length,
      preview: typeof parsed.data === 'string' ? parsed.data.slice(0, 50) : JSON.stringify(parsed.data).slice(0, 50),
    };
  } catch { return null; }
}

/**
 * 列出所有自动保存的 key
 */
export function listAutoSaves() {
  const saves = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k?.startsWith('fucai3d_autosave_')) {
      const name = k.replace('fucai3d_autosave_', '');
      saves.push({ key: name, ...(getAutoSaveInfo(name) || {}) });
    }
  }
  return saves;
}
